import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Package, Search, Loader, Barcode, MapPin, ArrowLeft } from 'lucide-react';
import { ItemsDto } from '../../types/items';
import { itemsService } from '../../services/itemsService';
import { useTranslation } from '../../contexts/I18nProvider';

export interface LocationInfo {
  locationName: string;
  warehouseID: string;
  availableQuantity: number;
}

interface ItemSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: ItemsDto, location: LocationInfo) => void;
  title?: string;
  fetchLocations: (itemNo: string) => Promise<LocationInfo[]>;
  /**
   * Aktivt lager. Når satt, viser varesøket kun varer som har ItemBins-rad i dette lageret
   * (TW 36386708). Utelat for master-data-skjermer som skal søke i hele vareregisteret.
   */
  warehouseId?: string | null;
  /**
   * Hopp over lokasjonssteget: `onSelect` kalles rett etter varevalg med tom lokasjon
   * (`locationName: ''`). Brukes i GO-fritelling der lokasjon er valgfri (TW 36386710).
   */
  skipLocationStep?: boolean;
}

type Step = 'search' | 'selectLocation';

export const ItemSearchModal: React.FC<ItemSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  title,
  fetchLocations,
  warehouseId,
  skipLocationStep = false
}) => {
  const { t } = useTranslation();
  const defaultTitle = title || t('warehouse.itemSearch.title');
  
  // Step management
  const [step, setStep] = useState<Step>('search');
  const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Location state
  const [locations, setLocations] = useState<LocationInfo[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && step === 'search') {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, step]);

  // Handle ESC key to close modal or go back
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (step === 'selectLocation') {
          handleBack();
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, step]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state
      setStep('search');
      setSelectedItem(null);
      setSearchTerm('');
      setItems([]);
      setError(null);
      setHasSearched(false);
      setLoading(false);
      setLocations([]);
      setLoadingLocations(false);
      setLocationError(null);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    }
  }, [isOpen]);

  // Perform search
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setItems([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await itemsService.searchCurrentItems(term, warehouseId);
      setItems(results);
      setHasSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      setError(t('warehouse.itemSearch.errors.searchFailed'));
      setItems([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  // Handle search input with debounce
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle item selection - go to step 2
  const handleItemSelect = async (item: ItemsDto) => {
    if (skipLocationStep) {
      onSelect(item, { locationName: '', warehouseID: warehouseId || '', availableQuantity: 0 });
      onClose();
      return;
    }
    setSelectedItem(item);
    setStep('selectLocation');
    setLoadingLocations(true);
    setLocationError(null);
    setLocations([]);

    try {
      const fetchedLocations = await fetchLocations(item.itemNo);
      setLocations(fetchedLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocationError(t('warehouse.itemSearch.errors.locationsFailed'));
    } finally {
      setLoadingLocations(false);
    }
  };

  // Handle location selection - final selection
  const handleLocationSelect = (location: LocationInfo) => {
    if (selectedItem) {
      onSelect(selectedItem, location);
      onClose();
    }
  };

  // Handle back button
  const handleBack = () => {
    setStep('search');
    setSelectedItem(null);
    setLocations([]);
    setLocationError(null);
  };

  // Handle click on backdrop
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              {step === 'selectLocation' ? (
                <>
                  <MapPin className="h-6 w-6 text-blue-500" />
                  <h2 className="text-lg font-semibold">{t('warehouse.itemSearch.selectLocation')}</h2>
                </>
              ) : (
                <>
                  <Package className="h-6 w-6 text-blue-500" />
                  <h2 className="text-lg font-semibold">{defaultTitle}</h2>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={t('common.buttons.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* STEP 1: ITEM SEARCH */}
          {step === 'search' && (
            <>
              {/* Search Input */}
              <div className="p-6 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={t('warehouse.itemSearch.placeholders.searchItems')}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {searchTerm && (
                  <div className="mt-2 text-sm text-gray-600">
                    {t('warehouse.itemSearch.messages.autoSearch')}
                  </div>
                )}
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader className="animate-spin h-10 w-10 text-blue-500 mb-3" />
                    <span className="text-gray-600">{t('warehouse.itemSearch.messages.searching')}</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="text-red-500 mb-2">
                      <X className="h-12 w-12 mx-auto mb-3" />
                    </div>
                    <p className="text-gray-700 font-medium">{error}</p>
                  </div>
                ) : !hasSearched ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">{t('warehouse.itemSearch.messages.enterSearchTerm')}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {t('warehouse.itemSearch.messages.searchHelp')}
                    </p>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">{t('warehouse.itemSearch.messages.noItemsFound')}</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {t('warehouse.itemSearch.messages.tryDifferentSearch')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 mb-4">
                      {t('warehouse.itemSearch.messages.foundItems', { '0': items.length.toString() })}:
                    </div>

                    {items.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleItemSelect(item)}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <Package className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold font-mono text-gray-900 group-hover:text-blue-700">
                                {item.itemNo}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {item.description1 || t('warehouse.itemSearch.labels.noDescription')}
                              </div>

                              {/* Additional details */}
                              <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-500">
                                {item.itemBarCode && (
                                  <div className="flex items-center">
                                    <Barcode className="h-3 w-3 mr-1" />
                                    <span>{item.itemBarCode}</span>
                                  </div>
                                )}

                                {item.stdLocationName && (
                                  <div className="flex items-center">
                                    <span className="font-medium mr-1">{t('warehouse.itemSearch.labels.stdLocation')}:</span>
                                    <span>{item.stdLocationName}</span>
                                  </div>
                                )}

                                {item.itemUnitTxt && (
                                  <div className="flex items-center">
                                    <span className="font-medium mr-1">{t('warehouse.itemSearch.labels.unit')}:</span>
                                    <span>{item.itemUnitTxt}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Quantity info on the right */}
                          {(item.quantity !== undefined || item.quantityAvailable !== undefined) && (
                            <div className="text-right ml-4 flex-shrink-0">
                              {item.quantityAvailable !== undefined && (
                                <div className="font-semibold text-green-600">
                                  {item.quantityAvailable} {t('warehouse.itemSearch.labels.available')}
                                </div>
                              )}
                              {item.quantity !== undefined && (
                                <div className="text-sm text-gray-500">
                                  {item.quantity} {t('warehouse.itemSearch.labels.total')}
                                </div>
                              )}
                              {item.quantityReserved !== undefined && item.quantityReserved > 0 && (
                                <div className="text-sm text-orange-600">
                                  {item.quantityReserved} {t('warehouse.itemSearch.labels.reserved')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <div className="text-sm text-gray-500">
                  {t('warehouse.itemSearch.messages.pressEscToClose')}
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.buttons.cancel')}
                </button>
              </div>
            </>
          )}

          {/* STEP 2: LOCATION SELECTION */}
          {step === 'selectLocation' && selectedItem && (
            <>
              {/* Selected Item Display */}
              <div className="p-6 border-b bg-blue-50">
                <button
                  onClick={handleBack}
                  className="flex items-center text-blue-600 hover:text-blue-700 mb-3 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  <span className="text-sm font-medium">{t('warehouse.itemSearch.buttons.backToSearch')}</span>
                </button>

                <div className="flex items-start space-x-3">
                  <Package className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-lg font-mono text-gray-900">
                      {selectedItem.itemNo}
                    </div>
                    <div className="text-gray-700 mt-1">
                      {selectedItem.description1 || t('warehouse.itemSearch.labels.noDescription')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Results */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingLocations ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader className="animate-spin h-10 w-10 text-blue-500 mb-3" />
                    <span className="text-gray-600">{t('warehouse.itemSearch.messages.loadingLocations')}</span>
                  </div>
                ) : locationError ? (
                  <div className="text-center py-12">
                    <div className="text-red-500 mb-2">
                      <X className="h-12 w-12 mx-auto mb-3" />
                    </div>
                    <p className="text-gray-700 font-medium mb-4">{locationError}</p>
                    <button
                      onClick={handleBack}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      {t('warehouse.itemSearch.buttons.backToSearch')}
                    </button>
                  </div>
                ) : locations.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">{t('warehouse.itemSearch.messages.noLocationsWithStock')}</p>
                    <p className="text-sm text-gray-400 mt-2 mb-6">
                      {t('warehouse.itemSearch.messages.itemNotInStock')}
                    </p>
                    <button
                      onClick={handleBack}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      {t('warehouse.itemSearch.buttons.backToSearch')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 mb-4">
                      {t('warehouse.itemSearch.messages.foundLocations', { '0': locations.length.toString() })}:
                    </div>

                    {locations.map((location, index) => (
                      <div
                        key={`${location.locationName}-${location.warehouseID}-${index}`}
                        onClick={() => handleLocationSelect(location)}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <MapPin className="h-6 w-6 text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-xl text-gray-900 group-hover:text-blue-700">
                                {location.locationName}
                              </div>

                              {location.warehouseID && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {t('warehouse.itemSearch.labels.warehouse')}: {location.warehouseID}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Available quantity on the right */}
                          <div className="text-right ml-4 flex-shrink-0">
                            <div className="font-bold text-2xl text-green-600">
                              {location.availableQuantity}
                            </div>
                            <div className="text-sm text-gray-500">
                              {t('warehouse.itemSearch.labels.available')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={handleBack}
                  className="flex items-center px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.buttons.back')}
                </button>
                <div className="text-sm text-gray-500">
                  {t('warehouse.itemSearch.messages.pressEscToGoBack')}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
